import { useState, useMemo, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth, useCurrentUser } from '@/context/AuthContext'
import { useCurrentMatchday } from '@/hooks/useCurrentMatchday'
import { usePickableMatchdays, hasEarlyFixtureGap } from '@/hooks/usePickableMatchdays'
import { useFixtureExclusions } from '@/hooks/useFixtureExclusions'
import { useCurrentMatchdayFixtures } from '@/hooks/useCurrentMatchdayFixtures'
import { useTeams } from '@/hooks/useTeams'
import { useUserSelections } from '@/hooks/useUserSelections'
import { useTDDIneligibleTeams } from '@/hooks/useTDDIneligibleTeams'
import { useCountdown } from '@/hooks/useCountdown'
import { useSyncFixturesWithOdds } from '@/hooks/useSyncFixturesWithOdds'
import { useTeamForm } from '@/hooks/useTeamForm'
import { useCurrentSeasonStandings } from '@/hooks/useCurrentSeasonStandings'
import { getApiSeasonYear } from '@/lib/season'
import { useSeason } from '@/context/SeasonContext'
import { getDisplayName, getFormalName } from '@/lib/teams'
import { useLeagueConfig } from '@/hooks/useLeagueConfig'
import { toast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'
import { MatchdaySelector } from './components/MatchdaySelector'
import { MatchdayMakePick } from './components/MatchdayMakePick'
import { MatchdayWatchMode } from './components/MatchdayWatchMode'
import { FirstPickBanner } from './components/FirstPickBanner'

export function MatchdaySection() {
  const { user } = useAuth()
  const currentUser = useCurrentUser()
  const { season } = useSeason()
  const apiYear = getApiSeasonYear(season)
  const queryClient = useQueryClient()

  const [isSubmitting, setIsSubmitting] = useState(false)

  // League config for competition filtering
  const { seasonSplits, config: leagueConfig, competitionId, scoringRules, isLoading: isLoadingConfig } = useLeagueConfig()

  // Data hooks
  const { data: currentMatchday, isLoading: isLoadingMatchday, refetch: refetchMatchday } = useCurrentMatchday(season, competitionId)
  const { data: pickableMatchdays, isLoading: isLoadingPickable } = usePickableMatchdays(season, competitionId, currentUser?.leagueId)
  const { excludedFixtureIds } = useFixtureExclusions(currentUser?.leagueId, season)

  // Selected matchday for navigation — defaults to currentMatchday
  const [selectedMatchday, setSelectedMatchday] = useState<number | null>(null)
  const activeMatchday = selectedMatchday ?? currentMatchday ?? 0

  // Sync selectedMatchday: on initial load, and when the selected matchday
  // drops out of the pickable list (e.g. all its fixtures finished).
  useEffect(() => {
    if (!currentMatchday) return
    if (selectedMatchday === null) {
      setSelectedMatchday(currentMatchday)
      return
    }
    if (
      pickableMatchdays &&
      pickableMatchdays.length > 0 &&
      !pickableMatchdays.some((md) => md.matchday === selectedMatchday)
    ) {
      setSelectedMatchday(currentMatchday)
    }
  }, [currentMatchday, selectedMatchday, pickableMatchdays])

  // Load fixtures for the active (selected) matchday
  const { data: fixtures, isLoading: isLoadingFixtures, refetch: refetchFixtures } = useCurrentMatchdayFixtures(
    activeMatchday,
    season,
    competitionId
  )
  const { data: teamsData, isLoading: isLoadingTeams } = useTeams()
  const {
    selectionData,
    getTeamUsageCount,
    canSelectTeam,
    canUseTddForTeam,
    refetch: refetchSelections,
  } = useUserSelections(user?.id, currentUser?.leagueId, season)
  const { data: manualIneligible } = useTDDIneligibleTeams(undefined, currentUser?.leagueId)
  const { syncFixturesWithOdds, syncAllFixtures, isLoading: isSyncing, isSyncingAll } = useSyncFixturesWithOdds()
  const { teamForms } = useTeamForm(competitionId)
  const { data: standingsData } = useCurrentSeasonStandings(apiYear)

  // Auto-sync all fixtures once when league has no upcoming fixtures.
  // Triggers for: new leagues (0 fixtures) and incomplete syncs (only past fixtures).
  // Ref ensures we only attempt once per session — no retry loop.
  const hasAttemptedInitialSync = useRef(false)
  useEffect(() => {
    if (isLoadingConfig || isLoadingMatchday || isLoadingPickable || hasAttemptedInitialSync.current) return
    if (!competitionId || isSyncingAll) return
    // Skip if there are upcoming pickable matchdays — full sync already happened
    if (pickableMatchdays && pickableMatchdays.length > 0) return
    hasAttemptedInitialSync.current = true

    syncAllFixtures(season).then(() => {
      queryClient.invalidateQueries({ queryKey: ['current-matchday'] })
      queryClient.invalidateQueries({ queryKey: ['current-matchday-fixtures'] })
      queryClient.invalidateQueries({ queryKey: ['pickable-matchdays'] })
      queryClient.invalidateQueries({ queryKey: ['manager-upcoming-fixtures'] })
    }).catch(() => {
      // Failed — user can retry via the button in the empty state
    })
  }, [isLoadingConfig, isLoadingMatchday, isLoadingPickable, currentMatchday, competitionId, isSyncingAll, season, syncAllFixtures, queryClient, pickableMatchdays])

  // Auto-sync fixtures + odds once per matchday per session.
  // Uses a Set so navigating back to an already-synced matchday doesn't re-trigger.
  const syncedMatchdaysRef = useRef<Set<number>>(new Set())
  useEffect(() => {
    if (!activeMatchday || syncedMatchdaysRef.current.has(activeMatchday)) return
    syncedMatchdaysRef.current.add(activeMatchday)

    syncFixturesWithOdds(season, activeMatchday)
      .then(() => refetchFixtures())
      .catch((err) => console.error('Auto-sync failed:', err))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMatchday])

  // Build ineligible set for TDD
  const ineligibleSet = useMemo(
    () => new Set((manualIneligible || []).map((n) => n.toLowerCase())),
    [manualIneligible]
  )

  // Compute team histories from user selections
  const teamHistories = useMemo(() => {
    const histories: { [teamName: string]: { picks: number; wins: number; draws: number; losses: number; pointsEarned: number } } = {}

    for (const selection of selectionData.selections) {
      const teamName = selection.team_name
      if (!histories[teamName]) {
        histories[teamName] = { picks: 0, wins: 0, draws: 0, losses: 0, pointsEarned: 0 }
      }
      histories[teamName].picks++
      histories[teamName].pointsEarned += selection.points_earned || 0

      if (selection.match_result === 'win') {
        histories[teamName].wins++
      } else if (selection.match_result === 'draw') {
        histories[teamName].draws++
      } else if (selection.match_result === 'loss') {
        histories[teamName].losses++
      } else if (selection.processed && selection.points_earned !== null) {
        const pts = selection.points_earned
        const multiplier = selection.used_double_down ? 2 : 1

        if (pts >= 3 * multiplier) {
          histories[teamName].wins++
        } else if (pts >= 1 * multiplier && pts < 3 * multiplier) {
          histories[teamName].draws++
        } else {
          histories[teamName].losses++
        }
      }
    }

    return histories
  }, [selectionData.selections])

  // Compute TDD usage by season half using league config splits
  const tddUsageByHalf = useMemo(() => {
    let apertura = 0
    let clausura = 0
    const splitPoint = seasonSplits?.[0]?.endMatchday ?? Math.floor(leagueConfig.total_matchdays / 2)

    for (const selection of selectionData.selections) {
      if (selection.used_double_down) {
        if (selection.matchday <= splitPoint) {
          apertura++
        } else {
          clausura++
        }
      }
    }

    return { apertura, clausura }
  }, [selectionData.selections, seasonSplits, leagueConfig.total_matchdays])

  // Filter fixtures to only those with valid EPL teams
  const matchdayFixtures = useMemo(() => {
    if (!fixtures || !activeMatchday) return []
    const allowedTeamNames = new Set((teamsData?.teams || []).map((t) => t.name.toLowerCase()))
    if (allowedTeamNames.size === 0) return fixtures
    return fixtures.filter(
      (f) =>
        allowedTeamNames.has(f.home_team.toLowerCase()) &&
        allowedTeamNames.has(f.away_team.toLowerCase())
    )
  }, [fixtures, activeMatchday, teamsData?.teams])

  // Compute deadline: 1 minute before earliest non-excluded kickoff of the active matchday
  const deadlineDate = useMemo(() => {
    if (matchdayFixtures.length === 0) return null
    // Filter out excluded fixtures when computing deadline
    const nonExcluded = matchdayFixtures.filter((f) => !excludedFixtureIds.has(f.id))
    const fixturesForDeadline = nonExcluded.length > 0 ? nonExcluded : matchdayFixtures
    const earliest = fixturesForDeadline.reduce((min, f) => {
      const t = new Date(f.kickoff_time)
      return t < min ? t : min
    }, new Date(fixturesForDeadline[0].kickoff_time))
    return new Date(earliest.getTime() - 60000)
  }, [matchdayFixtures, excludedFixtureIds])

  const countdown = useCountdown(deadlineDate)

  // Detect early fixture: one fixture kicks off significantly earlier than the rest
  const earlyFixture = useMemo(() => {
    const kickoffs = matchdayFixtures.map((f) => new Date(f.kickoff_time))
    if (!hasEarlyFixtureGap(kickoffs)) return null

    const sorted = [...matchdayFixtures].sort(
      (a, b) => new Date(a.kickoff_time).getTime() - new Date(b.kickoff_time).getTime()
    )
    return {
      homeTeam: getDisplayName(sorted[0].home_team),
      awayTeam: getDisplayName(sorted[0].away_team),
      kickoffTime: new Date(sorted[0].kickoff_time),
    }
  }, [matchdayFixtures])

  // Pre/post deadline for the selected matchday
  const isPreDeadline = !countdown.isExpired && countdown.hasDeadline
  const showPickUI = isPreDeadline || !countdown.hasDeadline

  // TDD eligibility check combining ineligible list and per-team usage
  const isTddEligible = (teamName: string) => {
    const displayName = getDisplayName(teamName)
    const candidates = [displayName.toLowerCase(), teamName.toLowerCase()]
    return !candidates.some((n) => ineligibleSet.has(n))
  }

  // Get current matchday selection for TDD checks
  const activeMatchdaySelection = selectionData.selections.find((s) => s.matchday === activeMatchday)

  const canUseTddForTeamWrapped = (teamName: string) => {
    const displayName = getDisplayName(teamName)

    if (!isTddEligible(teamName)) return false

    if (activeMatchdaySelection && getDisplayName(activeMatchdaySelection.team_name) === displayName && activeMatchdaySelection.used_double_down) {
      return true
    }

    return canUseTddForTeam(displayName)
  }

  // Submit handler — uses activeMatchday (the selected matchday)
  const handleSubmit = async (teamName: string, tddEnabled: boolean) => {
    if (!user) {
      toast({ title: 'Not Logged In', description: 'Please log in to submit your prediction.', variant: 'destructive' })
      return
    }

    const displayName = getDisplayName(teamName)
    const formalName = getFormalName(teamName)

    const currentSelection = selectionData.selections.find((s) => s.matchday === activeMatchday)
    const isUpdatingSameTeam = currentSelection
      ? getDisplayName(currentSelection.team_name) === displayName
      : false

    if (!isUpdatingSameTeam && !canSelectTeam(displayName)) {
      toast({ title: 'Team Limit Reached', description: `You have already selected ${displayName} twice this season.`, variant: 'destructive' })
      return
    }

    const isNewTddUsage = tddEnabled && !(isUpdatingSameTeam && currentSelection?.used_double_down)
    if (isNewTddUsage && !canUseTddForTeamWrapped(teamName)) {
      toast({ title: 'DD Not Available', description: `${displayName} is not eligible for Double Down.`, variant: 'destructive' })
      return
    }

    const fixture = matchdayFixtures.find(
      (f) => f.home_team === teamName || f.away_team === teamName
    )
    if (!fixture) return

    const predictedOutcome = fixture.home_team === teamName ? 'home' : 'away'

    try {
      setIsSubmitting(true)

      const predictionRecords = [
        {
          user_id: user.id,
          fixture_id: fixture.id,
          predicted_outcome: predictedOutcome,
          selected_team: formalName,
          used_double_down: tddEnabled,
          matchday: activeMatchday,
          season,
          league_id: currentUser?.leagueId,
        },
      ]

      const { error } = await supabase.functions.invoke('submit-predictions', {
        body: {
          predictions: predictionRecords,
          leagueId: currentUser?.leagueId,
        },
      })

      if (error) {
        // Extract server error message if available
        let message = 'Failed to submit your prediction. Please try again.'
        try {
          const body = await error.context?.json()
          if (body?.error) message = body.error
        } catch { /* use default message */ }
        toast({ title: 'Submission Failed', description: message, variant: 'destructive' })
        return
      }

      toast({ title: 'Pick Submitted!', description: `${displayName}${tddEnabled ? ' (DD)' : ''} for Matchday ${activeMatchday}.` })
      await refetchSelections()
    } catch {
      toast({ title: 'Submission Failed', description: 'An unexpected error occurred. Please try again.', variant: 'destructive' })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Fetch match_results for red card display in watch mode
  const [matchRedCards, setMatchRedCards] = useState<Record<string, { home: number; away: number }>>({})
  useEffect(() => {
    if (showPickUI || !activeMatchday) return

    const fetchRedCards = async () => {
      const { data } = await supabase
        .from('match_results')
        .select('fixture_id, red_cards_home, red_cards_away')
        .eq('matchday', activeMatchday)
        .eq('season', season)

      if (data) {
        const map: Record<string, { home: number; away: number }> = {}
        for (const r of data) {
          if (r.red_cards_home > 0 || r.red_cards_away > 0) {
            map[r.fixture_id] = { home: r.red_cards_home, away: r.red_cards_away }
          }
        }
        setMatchRedCards(map)
      }
    }

    fetchRedCards()
  }, [showPickUI, activeMatchday, season])

  // Loading state
  if (!user) {
    return (
      <div className="text-center py-12 text-zinc-500 dark:text-zinc-400">
        <p>Please log in to make your match predictions.</p>
      </div>
    )
  }

  if (isLoadingConfig || isLoadingMatchday || isLoadingFixtures || isLoadingTeams) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    )
  }

  if (!currentMatchday) {
    return (
      <div className="text-center py-12 text-zinc-500 dark:text-zinc-400">
        {isSyncingAll ? (
          <>
            <Loader2 className="h-6 w-6 animate-spin text-zinc-400 mx-auto mb-3" />
            <p className="text-lg font-medium mb-2">Syncing fixtures...</p>
            <p className="text-sm">Fetching fixture data for this competition. This may take a moment.</p>
          </>
        ) : (
          <>
            <p className="text-lg font-medium mb-2">No fixtures available yet</p>
            <p className="text-sm mb-4">Fixtures for this competition haven't been synced yet.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                hasAttemptedInitialSync.current = false
                syncAllFixtures(season).then(() => {
                  queryClient.invalidateQueries({ queryKey: ['current-matchday'] })
                  queryClient.invalidateQueries({ queryKey: ['current-matchday-fixtures'] })
                  queryClient.invalidateQueries({ queryKey: ['pickable-matchdays'] })
                }).catch(() => {})
              }}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Sync Fixtures
            </Button>
          </>
        )}
      </div>
    )
  }

  // Matchday navigation selector
  const selectorElement = pickableMatchdays && pickableMatchdays.length > 1 ? (
    <div className="mb-4">
      <MatchdaySelector
        matchdays={pickableMatchdays}
        selectedMatchday={activeMatchday}
        onSelect={setSelectedMatchday}
      />
    </div>
  ) : null

  // Check if the active matchday is completed (all fixtures finished)
  const activeMatchdayCompleted = pickableMatchdays?.find((md) => md.matchday === activeMatchday)?.isCompleted ?? false

  // Post-deadline: show the rich watching view with fixture scores + league picks
  if (!showPickUI) {
    const currentPick = selectionData.selections.find((s) => s.matchday === activeMatchday)

    return (
      <div>
        {selectorElement}
        <MatchdayWatchMode
          matchdayNumber={activeMatchday}
          fixtures={matchdayFixtures}
          teams={teamsData?.teams}
          deadlineDate={deadlineDate}
          isCompleted={activeMatchdayCompleted}
          countdown={countdown}
          season={season}
          redCards={matchRedCards}
          currentPick={currentPick ? {
            team_name: currentPick.team_name,
            used_double_down: currentPick.used_double_down,
          } : null}
        />
      </div>
    )
  }

  // Find active matchday selection
  const currentSelection = selectionData.selections.find((s) => s.matchday === activeMatchday)

  const isFirstPick = selectionData.selections.length === 0
  // Only show first-pick banner on the first unlocked matchday (the one the user
  // should actually pick for), not on locked or future matchdays.
  const firstUnlockedMatchday = pickableMatchdays?.find(md => !md.isLocked)?.matchday
  const showFirstPickBanner = isFirstPick && firstUnlockedMatchday != null && activeMatchday === firstUnlockedMatchday

  // Pre-deadline: show pick UI
  return (
    <div>
      {selectorElement}
      {showFirstPickBanner && (
        <FirstPickBanner
          matchdayNumber={firstUnlockedMatchday ?? activeMatchday}
          countdown={countdown}
          scoringRules={scoringRules}
        />
      )}
      <MatchdayMakePick
        matchdayNumber={activeMatchday}
        season={season}
        fixtures={matchdayFixtures}
        teams={teamsData?.teams}
        teamForms={teamForms}
        teamRanks={standingsData?.teamRanks}
        teamRecords={standingsData?.teamRecords}
        teamHistories={teamHistories}
        tddUsageByHalf={tddUsageByHalf}
        deadlineDate={deadlineDate}
        countdown={countdown}
        earlyFixture={earlyFixture}
        tddRemaining={selectionData.tddRemaining}
        currentSelection={currentSelection ? {
          team_name: currentSelection.team_name,
          used_double_down: currentSelection.used_double_down,
        } : null}
        getTeamUsage={getTeamUsageCount}
        canSelectTeam={canSelectTeam}
        isTddEligible={isTddEligible}
        canUseTddForTeam={canUseTddForTeamWrapped}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
      />
    </div>
  )
}
